/**
 * Content type definitions for colorscales.io
 * These types ensure type safety across all content data files
 */

/**
 * Launch phase type for environment-variable controlled pricing display
 * Aligned with plugin phases in src/constants/features/payment.ts:
 * - pre-launch: PRO locked, usage limits bypassed, no payment available
 * - beta: PRO locked, usage limits bypassed, collecting feedback
 * - early-bird: PRO available at $39 one-time via Figma Payments
 * - stable: PRO available at $49 one-time via Figma Payments
 *
 * Production (colorscales.io) should use `VITE_LAUNCH_MODE=stable`. Earlier phases remain in code for local/testing.
 */
export type LaunchPhase = 'pre-launch' | 'beta' | 'early-bird' | 'stable';

/** Inline link segment for rich text content */
export interface ContentLinkSegment {
  text: string;
  href: string;
  external?: boolean;
  ariaLabel?: string;
  wrap?: 'ins';
}

export type ContentSegment = string | ContentLinkSegment;

export type RichText = ContentSegment[];

export const isRichText = (value: RichText | string): value is RichText =>
  Array.isArray(value);

export interface HeaderContent {
  logo: string;
  navigation: {
    links: Array<{
      text: string;
      targetId: string;
    }>;
    cta: {
      text: string;
      targetId: string;
    };
  };
}

/**
 * Phase-specific header CTA overrides
 */
export interface HeaderPhaseContent {
  preLaunch: {
    cta: { text: string; targetId: string };
  };
  beta: {
    cta: { text: string; targetId: string };
  };
  earlyBird: {
    cta: { text: string; targetId: string };
  };
  stable: {
    cta: { text: string; targetId: string };
  };
}

export interface HeroCTA {
  text: string;
  action: string;
  /** Whether the CTA is an external link (opens in new tab) */
  isExternal?: boolean;
}

export interface HeroContent {
  title: {
    /** Short line above the main headline (inside H1) for brand visibility / SEO */
    eyebrow?: string;
    text: string;
    highlightedText: string;
    highlightClass?: string;
    suffix?: string;
  };
  subtitle: RichText | string;
  primaryCTA: HeroCTA;
}

/**
 * Phase-specific hero CTA overrides
 */
export interface HeroPhaseContent {
  preLaunch: {
    cta: HeroCTA;
  };
  beta: {
    cta: HeroCTA;
  };
  earlyBird: {
    cta: HeroCTA;
  };
  stable: {
    cta: HeroCTA;
  };
}

export interface DemoContent {
  title: string;
  instructions: string;
  previewTitle: string;
  ctaText: string;
  ctaButton: string;
}

export interface FeatureCard {
  title: string;
  body: string;
  benefits: Array<RichText | string>;
}

export interface FeaturesContent {
  title: string;
  features: FeatureCard[];
}

export interface WorkflowStep {
  number: string;
  title: string;
  description: string;
}

export interface HowItWorksContent {
  title: string;
  steps: WorkflowStep[];
  tagline: string;
}

export interface ImportExportContent {
  title: string;
  subtitle?: string;
  importSources: string[];
  exportDestinations: string[];
  tagline?: string;
}

export interface FeatureGridItem {
  icon: string;
  title: string;
  description: RichText | string;
}

export interface FeaturesGridContent {
  title: string;
  subtitle?: string;
  features: FeatureGridItem[];
}

export interface PriceMap {
  GBP: number;
  USD: number;
  EUR: number;
}

export interface TeaserConfig {
  earlyBird: string | null;
}

export interface PriceOverrides {
  earlyBird: { oneTime: number } | null;
}

export interface PricingFeature {
  feature: string;
  included: boolean | string;
  comingSoon?: boolean;
  strikethrough?: boolean;
  specialIcon?: 'pro-blue';
}

export interface PricingTier {
  name: string;
  subtitle?: string;
  /** Static price string (e.g., "Free") */
  price?: string | 'tbd';
  /** Monthly prices by currency */
  prices?: PriceMap;
  /** Yearly prices by currency (for annual billing option) */
  yearlyPrices?: PriceMap;
  /** Price period (e.g., "one-time", "/ forever") */
  period?: string;
  features: PricingFeature[];
  ctaText?: string;
  ctaSubtext?: string;
  /** Figma plugin link for CTAs */
  ctaLink?: string;
}

export interface FAQItem {
  question: string;
  answer: RichText | string;
}

/**
 * Phase-specific FAQ content
 * Allows different FAQs to be displayed based on launch phase
 */
export interface FAQPhaseContent {
  preLaunch: FAQItem[];
  beta: FAQItem[];
  earlyBird: FAQItem[];
  stable: FAQItem[];
}

// =============================================================================
// PRICING TYPES
// =============================================================================

/**
 * Unified pricing content that displays pricing table for all phases
 */
export interface PricingContent {
  /** Section title */
  title: string;
  /** Section subtitle */
  subtitle: string;
  /** Teaser banner configuration per phase */
  teaser: TeaserConfig;
  /** Pricing tiers (Basic and Pro) */
  tiers: {
    basic: PricingTier;
    pro: PricingTier;
  };
  /** Price overrides per phase (base: $49 one-time, early-bird: $39) */
  priceOverrides: PriceOverrides;
}

export interface EmailCaptureContent {
  title: string;
  subtitle: string;
  emailPlaceholder: string;
  buttonText: string;
  errorMessage?: string;
}

export interface FinalCTAContent {
  title: string;
  subtitle: string;
  benefits: string[];
  formTitle: string;
  emailCapture: EmailCaptureContent;
}

/**
 * Phase-specific Final CTA content
 */
export interface FinalCTAPhaseContent {
  beta: {
    title: string;
    subtitle: string;
    ctaText: string;
    ctaLink: string;
    ctaSubtext?: string;
  };
  stable: {
    title: string;
    subtitle: string;
    ctaText: string;
    ctaLink: string;
    ctaSubtext?: string;
  };
}

export interface FooterNavGroup {
  title: string;
  links: Array<{
    text: string;
    href: string;
    isExternal?: boolean;
  }>;
}

export interface FooterContent {
  logo: string;
  mission: string;
  copyright: string;
  navGroups: FooterNavGroup[];
  finePrint: {
    firstLine: RichText | string;
    secondLine: string;
    className: string;
  };
}


